import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import { createLevel } from '../../../redux/features/assessmentCmsSlice';

/**
 * Add questions to one class of a paper — either as a rung of a ladder, or as a
 * flat paper with no levels at all.
 *
 * ---------------------------------------------------------------------------
 * NEITHER THE LEVEL KEY NOR ITS POSITION IS TYPED ANY MORE
 *
 * This form used to ask for a free-text "Level name" and a hand-typed "Position
 * in the ladder", and warn that the position could never be changed afterwards.
 * Both were the wrong question:
 *
 *   * the name was slugified into `level_key`, which is what a report groups by,
 *     so "Basic", "basic " and "Beginner" produced three incomparable rungs;
 *   * the position is the one number in the whole editor that must never change,
 *     because every stored answer is filed under `position * 100 + question
 *     number`. Explaining that in helper text is not the same as preventing it.
 *
 * Picking a rung from the list the server ships settles both: the key and the
 * position come with it, so Basic is position 1 in every paper. The name stays
 * editable afterwards, because what a parent reads on the level card is
 * editorial — only the key and the position are structural.
 *
 * ---------------------------------------------------------------------------
 * "NO LEVELS" IS A REAL CHOICE, NOT AN ABSENCE
 *
 * A paper that should go straight from the class to the questions still gets one
 * level in the database: `assessment_questions.level_id` is NOT NULL, and a
 * question with no level has no id to file its answer under. The server creates
 * that one ungated level under the reserved key `single` — the same shape the KG
 * paper has always had — and the funnel skips the level screen when it sees it.
 *
 * Choosing it is not a trap. It occupies position 1, so a flat paper can grow a
 * ladder later without re-keying a single stored answer: the questions already
 * written become its first rung, and the author only has to rename it.
 */
const AddLevelDialog = ({
  open,
  onClose,
  onCreated,
  subject,
  existingLevels,
  trackMeta,
  category: initialCategory,
  levelCatalogue,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.assessmentCms);

  const categories = trackMeta?.categories || [];
  const isLadder = trackMeta?.isLadder !== false;
  // Memoised because the default `[]` would be a new array on every render, and
  // it is a dependency of the effect that pre-selects the next free rung — which
  // would then run forever.
  const rungs = useMemo(() => levelCatalogue || [], [levelCatalogue]);

  const [mode, setMode] = useState('ladder'); // ladder | flat
  const [levelKey, setLevelKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState(
    initialCategory !== undefined && initialCategory !== null
      ? Number(initialCategory)
      : categories.length
        ? categories[0].value
        : 0,
  );
  const [passMark, setPassMark] = useState(isLadder ? 6 : '');
  const [indicators, setIndicators] = useState(['']);

  const siblings = useMemo(
    () => (existingLevels || []).filter((l) => Number(l.category) === Number(category)),
    [existingLevels, category],
  );

  // A rung already used in THIS class is disabled rather than hidden, so an
  // author can see that Basic exists and is taken instead of wondering where it
  // went. The server enforces the same thing; this only saves them the round
  // trip to a rejected save.
  const takenKeys = useMemo(() => siblings.map((l) => l.levelKey), [siblings]);

  // The flat option is only honest while the class has nothing in it. Once a
  // rung exists, "no levels" would mean deleting it.
  const flatAvailable = siblings.length === 0;

  useEffect(() => {
    if (!flatAvailable && mode === 'flat') setMode('ladder');
  }, [flatAvailable, mode]);

  // Pre-select the first rung this class does not already have, so the common
  // case — building a ladder in order — needs no thought.
  useEffect(() => {
    if (mode !== 'ladder') return;
    if (levelKey && !takenKeys.includes(levelKey)) return;
    const next = rungs.find((r) => !takenKeys.includes(r.key));
    setLevelKey(next ? next.key : '');
  }, [mode, takenKeys, rungs, levelKey]);

  const selectedRung = rungs.find((r) => r.key === levelKey) || null;

  const submit = async () => {
    const flat = mode === 'flat';
    try {
      await dispatch(
        createLevel({
          subjectId: subject.id,
          levelKey: flat ? 'single' : levelKey,
          // Empty means "use the rung's own label", which the server applies.
          // A flat paper is named after the paper itself.
          displayName: displayName.trim() || (flat ? subject.label : ''),
          category,
          // A flat paper has nothing to unlock, so it is ungated by definition
          // rather than by the author remembering to clear the box.
          passMark: flat || passMark === '' ? null : Number(passMark),
          indicators: indicators.filter((p) => p.trim()),
        }),
      ).unwrap();
      enqueueSnackbar(
        flat ? 'Ready — add its questions next.' : `Level added. Add its questions next.`,
        { variant: 'success' },
      );
      onCreated();
      onClose();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not create the level', { variant: 'error' });
    }
  };

  const canSubmit = mode === 'flat' || Boolean(levelKey);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add questions
        <Typography variant="caption" color="text.secondary" display="block">
          {subject?.label}
          {categories.length > 0 &&
            ` · ${categories.find((c) => c.value === Number(category))?.label || ''}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {categories.length > 0 && (
            <TextField
              select
              fullWidth
              size="small"
              label="Which class"
              value={category}
              onChange={(e) => setCategory(Number(e.target.value))}
              helperText="Cannot be changed later. Pick the shared option to write one paper for the whole band."
            >
              {categories.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isLadder && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                How is this class&apos;s paper structured?
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(e, next) => next && setMode(next)}
              >
                <ToggleButton value="ladder">Levels a child climbs</ToggleButton>
                <ToggleButton value="flat" disabled={!flatAvailable}>
                  No levels — one paper
                </ToggleButton>
              </ToggleButtonGroup>
              {!flatAvailable && (
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                  This class already has a level, so it is a ladder.
                </Typography>
              )}
            </Box>
          )}

          {isLadder && mode === 'ladder' && (
            <TextField
              select
              fullWidth
              size="small"
              label="Which level"
              value={levelKey}
              onChange={(e) => setLevelKey(e.target.value)}
              helperText="Fixed once created — the position it sits at is what answers are filed under."
            >
              {rungs.map((rung) => {
                const taken = takenKeys.includes(rung.key);
                return (
                  <MenuItem key={rung.key} value={rung.key} disabled={taken}>
                    {rung.order}. {rung.label}
                    {taken ? ' — already added' : ''}
                  </MenuItem>
                );
              })}
            </TextField>
          )}

          <TextField
            fullWidth
            size="small"
            label="Name shown to parents"
            placeholder={
              mode === 'flat' ? subject?.label || 'Assessment' : selectedRung?.label || 'Basic'
            }
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText="Optional — leave it blank to use the name above. Editable at any time."
          />

          {siblings.length > 0 && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.neutral',
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                Already in this class
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {siblings
                  .slice()
                  .sort((a, b) => a.levelOrder - b.levelOrder)
                  .map((l) => `${l.levelOrder}. ${l.displayName}`)
                  .join('   →   ')}
              </Typography>
            </Box>
          )}

          {isLadder && mode === 'ladder' && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Pass mark"
              value={passMark}
              onChange={(e) => setPassMark(e.target.value)}
              helperText={
                passMark === ''
                  ? 'Empty means this level does not unlock the next one.'
                  : `A student scoring ${passMark} or more moves up to the next level.`
              }
            />
          )}

          <Box>
            <Typography variant="subtitle2">What this level means, for parents</Typography>
            <Typography variant="caption" color="text.secondary">
              Bullet points on the level card. Optional.
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {indicators.map((point, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={point}
                    onChange={(e) =>
                      setIndicators(indicators.map((p, i) => (i === index ? e.target.value : p)))
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() => setIndicators(indicators.filter((_, i) => i !== index))}
                  >
                    <Icon icon="mdi:close" width={18} />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" onClick={() => setIndicators([...indicators, ''])}>
                  Add a point
                </Button>
              </Box>
            </Stack>
          </Box>

          {mode === 'flat' ? (
            <Alert severity="info">
              The child goes straight from choosing the subject into the questions. You can
              still add levels later — these questions become the first one, and nothing
              already answered is affected.
            </Alert>
          ) : (
            <Alert severity="info">
              The class and the level are fixed once this exists. Lead reports identify a
              level by its position, so moving it later would re-label every answer already
              recorded against it. The name is not fixed and can be changed whenever.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !canSubmit}>
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLevelDialog;
